/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 컨텍스트 필드 클래스를 정의한다.
 *
 * @file /src/components/form/field/context/Context.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 10. 5.
 */
import * as parent from '@components/form/field/Field';
import { Select } from '../select/Select';
import { Set } from '../set/Set';
import { Remote } from '@models/store/list/Remote';
import { Local } from '@models/store/list/Local';
import { Text } from '../text/Text';
import { Color } from '../color/Color';
import { Template } from '../template/Template';
import { Html } from '@utils/Html';
import { Language } from '@utils/Language';
import './Context.css';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 컨텍스트 목록을 불러올 URL
     */
    url?: string;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Context extends parent.Field {
    /**
     * 기본 URL
     */
    static url: string = null;

    /**
     * 컴포넌트 확장타입
     */
    variant = 'context';

    /**
     * 목록을 불러올 URL
     */
    url: string;

    /**
     * 모듈 선택 필드
     */
    modules: Select = null;

    /**
     * 컨텍스트 선택 필드
     */
    contexts: Select = null;

    /**
     * 환경설정 필드셋
     */
    config: Set = null;

    /**
     * change 리스너 완료 대기 프라이빗 큐
     */
    #changeReady: Promise<void> = Promise.resolve();
    #resolveChangeReady: (() => void) | null = null;

    /**
     * Context 필드 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Context> = null) {
        super(properties);

        this.url = properties.url ?? Context.url ?? '/';
    }

    /**
     * 기본 URL 을 설정한다.
     *
     * @param url - 기본 URL
     */
    static setUrl(url: string): void {
        Context.url = url;
    }

    /**
     * 모듈 선택 필드를 가져온다.
     *
     * @returns 모듈 선택 필드
     */
    getModules(): Select {
        if (this.modules === null) {
            this.modules = new Select({
                store: new Remote({
                    provider: {
                        url: this.url,
                    },
                }),
                displayField: 'title',
                valueField: 'name',
                search: true,
                flex: 1,
                renderer: (_, records) => {
                    if (records !== null && Array.isArray(records) == false) {
                        const record = records;
                        return Html.icon(record.get('icon')).toHtml() + record.get('title');
                    }

                    return '';
                },
                listRenderer: (_, record) => {
                    return Html.icon(record.get('icon')).toHtml() + record.get('title');
                },
                listeners: {
                    change: async (me) => {
                        const record = await me.getValueToRecordOne();
                        this.getContexts().getStore().empty();

                        await this.getContexts().getStore().add(record?.get('contexts'));
                        this.getContexts().setDisabled(this.getContexts().getStore().getCount() == 0);
                    },
                },
            });
            this.modules.setParent(this);
            this.items ??= [];
            this.items.push(this.modules);
        }

        return this.modules;
    }

    /**
     * 컨텍스트 선택 필드를 가져온다.
     *
     * @returns 컨텍스트 선택 필드
     */
    getContexts(): Select {
        if (this.contexts === null) {
            this.contexts = new Select({
                store: new Local({
                    provider: {
                        dataFields: ['title', 'name'],
                    },
                }),
                displayField: 'title',
                valueField: 'name',
                search: true,
                disabled: true,
                flex: 1,
                listeners: {
                    change: async (me) => {
                        const record = await me.getValueToRecordOne();
                        const config = record?.get('config') ?? null;

                        if (config?.length > 0) {
                            this.getConfig().show();
                            await this.setConfigFields(config);
                        } else {
                            this.getConfig().hide();
                        }
                    },
                },
            });
            this.contexts.setParent(this);
        }

        return this.contexts;
    }

    /**
     * 추가 환경설정을 위한 필드셋을 가져온다.
     *
     * @returns 환경설정 필드셋
     */
    getConfig(): Set {
        if (this.config === null) {
            this.config = new Set({
                label: Language.get('texts.config'),
                inputName: this.name + '_config',
                hidden: true,
            });
            this.config.setParent(this);
        }

        return this.config;
    }

    /**
     * 환경설정 필드셋에 필드를 추가한다.
     *
     * @param config - 환경설정
     */
    async setConfigFields(config: { [key: string]: any }[]): Promise<void> {
        this.getConfig().removeItems();

        const fields = await this.getConfigFields(config);
        for (const field of fields) {
            this.getConfig().appendItem(field);
        }
    }

    /**
     * 필드설정을 이용하여 필드를 가져온다.
     *
     * @param config - 환경설정
     * @returns 환경설정 필드
     */
    async getConfigFields(config: { [key: string]: any }[]): Promise<parent.Field[]> {
        const context = (await this.getContexts().getValue()) ?? null;
        const fields: parent.Field[] = [];
        for (const item of config) {
            let field: parent.Field = null;
            switch (item.type) {
                case 'fieldset':
                    field = new Set({
                        label: item.label ?? 'UNKNOWN',
                        items: [...(await this.getConfigFields(item.items))],
                    });
                    break;

                case 'text':
                    field = new Text({
                        name: item.name,
                        label: item.label ?? 'UNKNOWN',
                        value: item.value ?? item.default,
                        flex: 1,
                    });
                    break;

                case 'color':
                    field = new Color({
                        name: item.name,
                        label: item.label ?? 'UNKNOWN',
                        value: item.value ?? item.default,
                        flex: 1,
                    });
                    break;

                case 'select':
                    field = new Select({
                        name: item.name,
                        label: item.label ?? 'UNKNOWN',
                        value: item.value ?? item.default,
                        store: new Local({
                            provider: {
                                dataFields: ['value', 'display'],
                                datas: ((options: { [key: string]: any }) => {
                                    const datas = [];
                                    for (const value in options) {
                                        datas.push([value, options[value]]);
                                    }

                                    return datas;
                                })(item.options),
                            },
                        }),
                    });
                    break;

                case 'template':
                    field = new Template({
                        name: item.name,
                        label: item.label ?? 'UNKNOWN',
                        value: item.value ?? item.default,
                        url: item.url ?? Template.url ?? '/',
                        owner: {
                            type: item.package.type,
                            name: item.package.name,
                        },
                        context: context,
                    });
                    break;

                default:
                    field = new Text({
                        name: item.name,
                        label: item.label ?? 'UNKNOWN',
                        value: typeof item.value == 'object' ? JSON.stringify(item.value) : item.value,
                        helpText: Language.getError('UNSUPPORTED_FIELD_TYPE', { type: item.type }),
                    });
                    break;
            }

            if (field !== null) {
                fields.push(field);
            }
        }

        return fields;
    }

    /**
     * 필드값을 지정한다.
     *
     * @param value - 값
     * @param is_init - 초기값여부
     */
    async setValue(
        value: { module: string; context: string; config: object | null } | null,
        is_init: boolean = false,
        is_update: boolean = true
    ): Promise<void> {
        await this.lockValue();

        if (is_update == true) {
            await this.getModules().setValue(value?.module ?? null);
            await this.getContexts().setValue(value?.context ?? null);
            await this.getConfig().setValue(value?.config ?? null);
        }

        await this.releaseValue(await this.getValue(), is_init);
    }

    /**
     * 필드값을 가져온다.
     *
     * @returns 필드값
     */
    async getValue(): Promise<{ module: string; context: string; config: object | null } | null> {
        await this.#changeReady;

        const module = (await this.getModules().getValue()) as string | null;
        const context = (await this.getContexts().getValue()) as string | null;

        if (module !== null && context !== null) {
            return {
                module: module,
                context: context,
                config: ((await this.getConfig().getValue()) ?? null) as object | null,
            };
        }

        return null;
    }

    /**
     * 필드영역을 렌더링한다.
     */
    async renderField(): Promise<void> {
        this.$getContent().append(this.getModules().$getComponent());
        await this.getModules().render();

        this.$getContent().append(this.getContexts().$getComponent());
        await this.getContexts().render();

        this.$getContent().append(this.getConfig().$getComponent());
        await this.getConfig().render(true);
    }
}
